import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { APP_AI_CONNECT_API_ENDPOINT } from "constants/api_urls";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Box, CircularProgress, Snackbar } from "@mui/material";
import FormFieldRenderer from "components/FormFieldRenderer";
import { aiPanelFormFields } from "./options";
import HotelsFilterMenu from "components/hotelFilterMenu";
import { ArrowDropDown } from "@mui/icons-material";
import TextInput from "components/TextInput";
import CustomButtonWithIcon from "components/CustomButtonWithIcon/Buttons";
import { getUserToken } from "utils/functions/userToken";
import AIPanelLayout from "./layout";
import { toggleAIPanel, toggleShowTC, updateAIPanelHotels, updateBiddingLevel, updateTriggerLoadTable } from "store/slices/aiConnect/aiConnect";
const AIPanel = () => {
  const token = getUserToken();
  // Ref
  const hotelButtonRef = useRef(null);
  //Selectors
  const { account_id = "" } = useSelector(
    (state) => state?.account?.account || {}
  );
  const { aiPanelHotels:hotels = [],biddingLevel='hotels', triggerLoadTable } = useSelector((state) => state?.aiConnect);
  const dispatch = useDispatch();
  const [openHotelMenu, setOpenHotelMenu] = useState(false);
  const [selectedHotelOrGroupType, setSelectedHotelOrGroupType] =
    useState(biddingLevel);
  const [selectedHotelsorGroups, setSelectedHotelsorGroups] = useState(hotels);
  const [isLoading, setIsLoading] = useState(false);

  const [updateBidPayload, setUpdateBidPayload] = useState({
    account_id: account_id,
    bidding_level: selectedHotelOrGroupType === "hotels" ? "HOTEL" : "GROUP",
  });

  // List Data
  const [biddingList, setBiddingList] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");
  const {
    hotel_list = [],
    group_list = [],
    currency_symbol = "",
    can_use_group,
  } = biddingList || {};
  const COMMONFORMFIELDS = [
    {
      id: "hotel",
      label: "Hotel",
      type: "select",
      placeholder: " Select a hotel ",
      options: hotel_list,
    },
  ];

    const [errors, setErrors] = useState({
        hotelId: false,
        status: false,
    });

    const {hotel_id = [], hotelList = [], groupList = [], level , group_id = []} = useSelector(state => state?.account);
    
      React.useEffect(() => {
        if(level === "HOTEL") {
          const selectedHotels = hotelList?.filter(h => hotel_id?.includes(h.hotel_id)).map(hotel => ({
            id: hotel?.hotel_id,
            name: hotel?.hotel_name,
            isSelected: true
          }));
          dispatch(updateBiddingLevel("hotels"))
          dispatch(updateAIPanelHotels(selectedHotels))
          setSelectedHotelsorGroups(selectedHotels);
        } else {
          const selectedGroups = groupList?.filter(g => g.id && group_id?.includes(g.id)).map(group => ({
            id: group?.id,
            name: group?.name,
            isSelected: true
          }));
          dispatch(updateBiddingLevel("groups"))
          dispatch(updateAIPanelHotels(selectedGroups))
          setSelectedHotelsorGroups(selectedGroups);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      },[])

  // Service to render Bidding List based on Account Id
  useEffect(() => {
    if (account_id) {
      setIsLoading(true);
      setUpdateBidPayload((prev) => ({ ...prev, account_id }));
      axios
        .get(
          `${APP_AI_CONNECT_API_ENDPOINT}/api/ai-connect/bidding-panel-info/${account_id}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((res) => {
          setBiddingList(res?.data || {});
          dispatch(toggleShowTC(res?.data?.show_t_c || false));
          setIsLoading(false);
        });
    }
  }, [account_id,token]);

  const handleChangeFieldValue = useCallback((field, value) => {
    setUpdateBidPayload((prev) => ({ ...prev, [field]: value }));
  }, []);

   useEffect(() => {
          handleChangeFieldValue("bidding_level",biddingLevel === "hotels"? "HOTEL": "GROUP");
          handleChangeFieldValue(biddingLevel === "hotels"? "hotel_id": "group_id",hotels.map((s) => s.id));
      }, [hotels,handleChangeFieldValue,biddingLevel]);
      

  const handleSave = useCallback(() => {
      const isEmpty = (value) => !value || (typeof value === "string" && value.trim() === "") || (Array.isArray(value) && value.length === 0);
      const newErrors = {
            hotelId: updateBidPayload?.hotel_id !== undefined ? isEmpty(updateBidPayload?.hotel_id) : false,
            groupId : updateBidPayload?.group_id !== undefined ? isEmpty(updateBidPayload?.group_id) : false,          
            status: isEmpty(updateBidPayload?.bidding_status),
      };
      setErrors(newErrors);
      const hasErrors = Object.values(newErrors).some(error => error);
      if (!hasErrors) {
          const finalPayload = {...updateBidPayload};
          if (biddingLevel === "hotels") {
              delete finalPayload?.group_id;
          }
          if (biddingLevel === "groups") {
              delete finalPayload?.hotel_id;
          }
          setIsLoading(true);
          axios
              .put(
                  `${APP_AI_CONNECT_API_ENDPOINT}/api/ai-connect/update-bids/`,
                  finalPayload,
                  {
                      headers: {
                          Authorization: `Bearer ${token}`,
                      },
                  }
              )
              .then((res) => {
                  setToastMessage("Updated Successfully");
                  setToastType("success");
                  setShowToast(true);
                  setIsLoading(false);
                  dispatch(updateTriggerLoadTable(!triggerLoadTable));
              })
              .catch((err) => {
                  setToastType("error");
                  setToastMessage(
                      err?.response?.data[0]?.errorMessage || err?.[0]?.errorMessage || err?.message || "Something went wrong"
                  );
                  setShowToast(true);
                  setIsLoading(false);
              });
      }
  }, [updateBidPayload,selectedHotelOrGroupType,token,triggerLoadTable]);

  return (
    <AIPanelLayout>
    <div style={{ position: "relative", pointerEvents: isLoading ? "none" : "auto" }}>
      {isLoading && (
        <Box
          position="absolute"
          width="calc(100% + 36px)"
          top={0}
          left={-18}
          right={0}
          bottom={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor="rgba(255, 255, 255, 0.5)"
          zIndex={1000}
          height="115%"
        >
          <CircularProgress />
        </Box>
      )}
      <div className="form-title">EDIT YOUR BIDDING OPTIONS</div>
      <div className="form-button-container">
        <div className="formGrid">
          <div className="common-field-container">
            {COMMONFORMFIELDS.map((field) => {
              if (field.id === "hotel") {
                return (
                  <div className="form-grid-item bidding_multiplier_height">
                    <div className="mul-form-label">Hotels</div>
                    <div ref={hotelButtonRef}>
                      <TextInput
                        placeholder={field.placeholder}
                        type={"text"}
                        readOnly
                        value={
                          selectedHotelsorGroups.length > 0
                            ? selectedHotelsorGroups.length === 1
                              ? selectedHotelsorGroups[0].name
                              : `${selectedHotelsorGroups.length} ${biddingLevel} selected`
                            : ""
                        }
                        sx={{
                          backgroundColor: "white",
                          fontSize: "12px",
                          fontWeight: "400",
                          paddingLeft: "4px",
                        }}
                        endAdornment={<ArrowDropDown />}
                        onClick={(e) => {
                          setOpenHotelMenu(true);
                        }}
                        error={
                          selectedHotelOrGroupType === "hotels"
                            ? errors.hotelId
                            : errors.groupId
                        }
                      />
                    </div>
                    {hotel_list?.length ? (
                      <HotelsFilterMenu
                        allHotels={hotel_list?.map((hotel) =>
                          selectedHotelsorGroups?.find(
                            (h) => h.id === hotel.id
                          ) || hotel_list.length === 1
                            ? { ...hotel, isSelected: true }
                            : { ...hotel, isSelected: false }
                        )}
                        allGroups={group_list?.map((group) =>
                          selectedHotelsorGroups?.find((h) => h.id === group.id)
                            ? { ...group, isSelected: true }
                            : { ...group, isSelected: false }
                        )}
                        showGroups={group_list.length !== 0 && can_use_group}
                        anchorEl={hotelButtonRef.current}
                        open={openHotelMenu}
                        onClose={() => {
                          setOpenHotelMenu(false);
                        }}
                        onApply={(selected) => {
                          dispatch(updateAIPanelHotels(selected));
                          setSelectedHotelsorGroups(selected);
                          dispatch(
                            updateBiddingLevel(
                              selected.every((s) => s?.type === "HOTEL")
                                ? "hotels"
                                : "groups"
                            )
                          );
                          handleChangeFieldValue(
                            selectedHotelOrGroupType === "hotels"
                              ? "hotel_id"
                              : "group_id",
                            selected.map((s) => s.id)
                          );
                        }}
                        setSelectedHotelOrGroupType={(type) => {
                          setSelectedHotelOrGroupType(type);
                        }}
                        selectedHotelOrGroupType={selectedHotelOrGroupType}
                      />
                    ) : (
                      ""
                    )}
                  </div>
                );
              }
              return null;
            })}
          </div>
          <div className="fields-container bidding_multiplier_height">
            {aiPanelFormFields?.map((field) => {
              let newField = { ...field };

              if (
                updateBidPayload?.bidding_strategy?.bidding_type === "NO_BID" ||
                updateBidPayload?.bidding_strategy?.bidding_type === "MANUAL"
              ) {
                newField = { ...newField, numberUnit: currency_symbol };
              }

              return (
                <div className="form-grid-item">
                  <FormFieldRenderer
                    item={newField}
                    options={field?.options || []}
                    hotelMenuOpen={openHotelMenu}
                    onHotelMenuClose={() => {
                      setOpenHotelMenu(false);
                    }}
                    onChange={(val) => {
                      handleChangeFieldValue("bidding_status", val);
                    }}
                    labelKey={"label"}
                    errors={errors}
                  />
                </div>
              );
            })}
          </div>
        </div>
        {/* {!!updateBidPayload?.[
          selectedHotelOrGroupType === "hotels" ? "hotel_id" : "group_id"
        ]?.length &&
          updateBidPayload?.bidding_status && ( */}
        <div style={{ display: "flex", gap: 10, marginBottom: "-20px" }}>
          <CustomButtonWithIcon
            color="#b0acac"
            hoverColor="#12794F"
            titleColor="#373F41"
            hoverTitleColor={"white"}
            width={"85px"}
            height={"24px"}
            borderRadius="20px"
            textStyle={{
              fontFamily: "Inter",
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "14.52px",
              textAlign: "center",
            }}
            onClick={handleSave}
          >
            Save
          </CustomButtonWithIcon>
          <CustomButtonWithIcon
            color="#b0acac"
            hoverColor="#12794F"
            titleColor="#373F41"
            hoverTitleColor={"white"}
            width={"85px"}
            height={"24px"}
            borderRadius="20px"
            textStyle={{
              fontFamily: "Inter",
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "14.52px",
              textAlign: "center",
            }}
            onClick={() => {
              dispatch(toggleAIPanel(false));
            }}
          >
            Cancel
          </CustomButtonWithIcon>
        </div>
        {/* )} */}
      </div>
      <Snackbar
        open={showToast}
        autoHideDuration={3000}
        onClose={() => {
          setShowToast(false);
        }}
      >
        <Alert severity={toastType} variant="filled" sx={{ width: "100%" }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </div>
    </AIPanelLayout>
  );
};

export default AIPanel;
